import { Package, Clock, AlertTriangle, CheckCircle, Users } from 'lucide-react';
import type { SupplierBreakdown } from '../../api';

interface SupplierTableProps {
  bySupplier: SupplierBreakdown[];
  isLoading?: boolean;
  limit?: number;
}

/**
 * Format milliseconds to human-readable time string
 */
function formatDuration(ms: number | null): string {
  if (ms === null || ms === 0) return '-';

  const hours = ms / (1000 * 60 * 60);
  if (hours < 1) {
    const minutes = Math.round(ms / (1000 * 60));
    return `${minutes}m`;
  }
  if (hours < 24) {
    return `${Math.round(hours)}h`;
  }

  const days = hours / 24;
  return `${Math.round(days * 10) / 10}d`;
}

/**
 * Get ranking badge color based on position
 */
function getRankColor(index: number): { bg: string; text: string; border: string } {
  switch (index) {
    case 0:
      return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' };
    case 1:
      return { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' };
    case 2:
      return { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' };
    default:
      return { bg: 'bg-gray-50', text: 'text-gray-500', border: 'border-gray-100' };
  }
}

export function SupplierTable({ bySupplier, isLoading, limit = 10 }: SupplierTableProps) {
  const displayedSuppliers = bySupplier.slice(0, limit);
  const maxTotal = displayedSuppliers[0]?.total || 1;

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <div className="h-4 bg-gray-200 rounded w-44 mb-1 animate-pulse" />
        <div className="h-3 bg-gray-200 rounded w-32 mb-6 animate-pulse" />
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl animate-pulse">
              <div className="w-8 h-8 bg-gray-200 rounded-lg" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
                <div className="h-2 bg-gray-100 rounded-full w-full" />
              </div>
              <div className="w-12 h-6 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (bySupplier.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Top Distributors by Complaints</h3>
        <p className="text-xs text-gray-500 mb-4">Distributors ranked by complaint volume</p>
        <div className="h-64 flex flex-col items-center justify-center text-gray-400">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
            <Users className="w-8 h-8 text-gray-300" />
          </div>
          <p className="text-sm font-medium">No distributor data available</p>
          <p className="text-xs mt-1">Data will appear as complaints are submitted</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Top Distributors by Complaints</h3>
          <p className="text-xs text-gray-500">Distributors ranked by complaint volume</p>
        </div>
        <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
          {bySupplier.length} distributors
        </span>
      </div>

      {/* Supplier cards - responsive layout */}
      <div className="space-y-3">
        {displayedSuppliers.map((supplier, index) => {
          const pending = supplier.byStatus.submitted + supplier.byStatus.under_review;
          const resolved = supplier.byStatus.approved + supplier.byStatus.rejected;
          const resolutionRate = supplier.total > 0 ? Math.round((resolved / supplier.total) * 100) : 0;
          const barWidth = (supplier.total / maxTotal) * 100;
          const rankColor = getRankColor(index);

          return (
            <div
              key={supplier.supplierId}
              className="group relative bg-gray-50 hover:bg-gray-100/80 rounded-xl p-4 transition-all duration-200"
            >
              {/* Main content */}
              <div className="flex items-start gap-3">
                {/* Rank badge */}
                <div className={`flex-shrink-0 w-8 h-8 ${rankColor.bg} border ${rankColor.border} rounded-lg flex items-center justify-center`}>
                  <span className={`text-sm font-bold ${rankColor.text}`}>{index + 1}</span>
                </div>

                {/* Supplier info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm font-semibold text-gray-900 truncate">
                      {supplier.supplierName}
                    </span>
                  </div>

                  {/* Progress bar showing relative volume */}
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden mb-2">
                    <div
                      className="h-full bg-gradient-to-r from-gray-400 to-gray-500 rounded-full transition-all duration-500"
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>

                  {/* Stats row */}
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 text-amber-500" />
                      <span className="text-gray-600">
                        <span className="font-semibold text-amber-600">{pending}</span> pending
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      <span className="text-gray-600">
                        <span className="font-semibold text-green-600">{resolved}</span> resolved
                      </span>
                    </div>
                    {supplier.avgResolutionTime && supplier.avgResolutionTime > 0 && (
                      <div className="flex items-center gap-1 text-gray-400">
                        <Clock className="w-3 h-3" />
                        <span>{formatDuration(supplier.avgResolutionTime)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Total count and resolution rate */}
                <div className="flex-shrink-0 text-right">
                  <div className="text-lg font-bold text-gray-900">{supplier.total}</div>
                  <div className="text-xs text-gray-500">{resolutionRate}% resolved</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      {bySupplier.length > limit && (
        <div className="mt-4 pt-4 border-t border-gray-100 text-center">
          <span className="text-xs text-gray-500">
            Showing top {limit} of {bySupplier.length} distributors
          </span>
        </div>
      )}
    </div>
  );
}
